// The Inbox as a location names it (#808): which kinds it shows, and which item its
// drawer holds, in which context.
//
// **Every waiting kind is a row of `INBOX_KINDS`**, and a `Record` over `InboxKind`, so a
// kind added to `api/inbox.ts` without one here does not type-check. The row says what
// the kind is filtered as, which of the console's reads its drawer answers from, and —
// for the two kinds that had screens of their own — the old path that now opens its
// filter. What a kind looks like is `inboxKinds.tsx`'s, kept apart so this module stays
// free of components: `routes.ts` and `useConsole.ts` read it.
//
// The drawer's item is in the query, not in component state, because opening an item
// of another workspace selects that workspace first (`routes.ts::linkedContextOf`), and
// the console remounts under a new context: the location is what survives it. It is
// the same shape a mailed link is (`mail/links.py::Links.change`), `workspace=` or `org=`
// beside the item, so a link and a click open the drawer the same way.

import type { InboxItem, InboxKind } from "../api/inbox";
import type { ContextChoice } from "../api/session";

/**
 * Which console read holds a kind's answerable row, once its context is selected. `inbox`
 * is the inbox's own row, answered in the item's workspace: a held alarm (#849) has no
 * screen of its own to read it from.
 */
export type DrawerRead = "consent" | "changes" | "access" | "inbox" | null;

export interface InboxKindSpec {
  /** The filter's id, as `?show=` spells it. */
  readonly filter: string;
  /** What the filter is called, in the words the rail used for its old screen. */
  readonly label: string;
  /** What one item of the kind is, for the kind column's icon. */
  readonly one: string;
  /** The read the drawer answers from; null when the inbox item itself is enough. */
  readonly reads: DrawerRead;
  /** The screen this kind had before the Inbox took it (#795, decision 2), kept as a link. */
  readonly legacyPath?: string;
}

export const INBOX_KINDS: Readonly<Record<InboxKind, InboxKindSpec>> = {
  change_approval: {
    filter: "approvals",
    label: "Approvals",
    one: "Approval",
    reads: "changes",
    legacyPath: "/changes",
  },
  consent_request: {
    filter: "data-access",
    label: "Data access",
    one: "Data access request",
    reads: "consent",
    legacyPath: "/consent",
  },
  capability_request: {
    filter: "partner-access",
    label: "Partner access",
    one: "Partner access request",
    reads: "access",
  },
  invitation: { filter: "invitations", label: "Invitations", one: "Invitation", reads: null },
  held_alert: { filter: "held-alarms", label: "Held alarms", one: "Held alarm", reads: "inbox" },
};

const KIND_IDS = Object.keys(INBOX_KINDS) as InboxKind[];

export const INBOX_PATH = "/inbox";

/** The kinds a filter shows. */
export function kindsOf(filter: string): InboxKind[] {
  return KIND_IDS.filter((kind) => INBOX_KINDS[kind].filter === filter);
}

/** The filter's label, or null for a filter no kind has. */
export function filterLabel(filter: string): string | null {
  const kind = kindsOf(filter)[0];
  return kind === undefined ? null : INBOX_KINDS[kind].label;
}

/** Every filter, once each, in the registry's order. */
export function inboxFilters(): Array<{ id: string; label: string }> {
  const seen = new Set<string>();
  return KIND_IDS.flatMap((kind) => {
    const { filter, label } = INBOX_KINDS[kind];
    if (seen.has(filter)) return [];
    seen.add(filter);
    return [{ id: filter, label }];
  });
}

/** The item the drawer holds: what it is, and the context that answers it. */
export interface OpenItem {
  readonly kind: InboxKind;
  readonly id: string;
  /** Null for an item answered from no context: an invitation. */
  readonly context: ContextChoice | null;
}

export interface InboxView {
  readonly filter: string | null;
  readonly open: OpenItem | null;
}

function normalise(pathname: string): string {
  if (pathname === "" || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/** The kind whose old screen lived at `pathname`, if one did. */
export function legacyKindOf(pathname: string): InboxKind | null {
  const path = normalise(pathname);
  return KIND_IDS.find((kind) => INBOX_KINDS[kind].legacyPath === path) ?? null;
}

/**
 * The workspace a link names. A link written before the rename (#1192) says `?tenant=`, and
 * mail already queued still carries one, so for one release the old key is read too; a
 * link the console or the server writes says `?workspace=`.
 */
export function linkedWorkspaceOf(query: URLSearchParams): string | null {
  return query.get("workspace") || query.get(PRE_RENAME_WORKSPACE_KEY) || null;
}

/** The old key, read and never written. */
const PRE_RENAME_WORKSPACE_KEY = "tenant";

function contextIn(query: URLSearchParams): ContextChoice | null | "both" {
  const workspace = linkedWorkspaceOf(query);
  const org = query.get("org");
  if (workspace && org) return "both";
  if (workspace) return { workspace_id: workspace };
  if (org) return { org_id: org };
  return null;
}

/**
 * The Inbox view a location names, or null when it names none.
 *
 * `/inbox?show=<filter>&open=<kind>:<id>&workspace=<id>` (or `&org=<id>`), and the two old
 * paths: `/changes` opens the Approvals filter, and a change link — `?change=<id>&workspace=`
 * or `&org=`, what a mail or a Slack card carries — opens that change in the drawer;
 * `/consent` opens Data access. An item named with no context, or with both, opens
 * nothing: the drawer never guesses which context answers it.
 */
export function inboxViewOf(pathname: string, search: string): InboxView | null {
  const query = new URLSearchParams(search);
  const context = contextIn(query);
  const legacy = legacyKindOf(pathname);
  if (legacy !== null) {
    const filter = INBOX_KINDS[legacy].filter;
    const change = legacy === "change_approval" ? query.get("change") : null;
    if (!change || context === null || context === "both") return { filter, open: null };
    return { filter, open: { kind: "change_approval", id: change, context } };
  }
  if (normalise(pathname) !== INBOX_PATH) return null;
  const shown = query.get("show");
  const filter = shown !== null && kindsOf(shown).length > 0 ? shown : null;
  const named = query.get("open") ?? "";
  const colon = named.indexOf(":");
  const kind = named.slice(0, colon) as InboxKind;
  const id = named.slice(colon + 1);
  if (colon <= 0 || id === "" || !(kind in INBOX_KINDS) || context === "both") return { filter, open: null };
  const wantsContext = INBOX_KINDS[kind].reads !== null;
  if (wantsContext && context === null) return { filter, open: null };
  return { filter, open: { kind, id, context: wantsContext ? context : null } };
}

/** The location of a view: what the drawer's open and close, and a filter, navigate to. */
export function inboxPath(view: { filter?: string | null; open?: OpenItem | null }): string {
  const query = new URLSearchParams();
  if (view.filter) query.set("show", view.filter);
  const open = view.open ?? null;
  if (open !== null) {
    query.set("open", `${open.kind}:${open.id}`);
    if (open.context !== null && "workspace_id" in open.context) query.set("workspace", open.context.workspace_id);
    else if (open.context !== null) query.set("org", open.context.org_id);
  }
  const text = query.toString();
  return text === "" ? INBOX_PATH : `${INBOX_PATH}?${text}`;
}

/** The path of a filter on its own: what a link from another screen opens. */
export const filterPath = (filter: string): string => inboxPath({ filter });

/**
 * The context an item is answered in: a change an organisation's grant holds is the
 * organisation's; any other item is its workspace's; an invitation needs none.
 */
export function answeredIn(item: InboxItem): ContextChoice | null {
  if (item.kind === "invitation") return null;
  if (item.kind === "change_approval" && item.detail.holder.kind === "org") return { org_id: item.detail.holder.id };
  return item.workspace_id === null ? null : { workspace_id: item.workspace_id };
}

/** The drawer's item for an inbox row. */
export const openOf = (item: InboxItem): OpenItem => ({ kind: item.kind, id: item.id, context: answeredIn(item) });

/** The key a row and its drawer share, for the focus that returns to the row. */
export const openKey = (open: Pick<OpenItem, "kind" | "id">): string => `${open.kind}:${open.id}`;
